import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const { openCart, itemCount } = useCart();
  const location = useLocation();
  
  const navLinks = [
    { path: '/', label: 'home' },
    { path: '/products', label: 'products' },
    { path: '/delivery', label: 'delivery' },
    { path: '/about', label: 'about' },
    { path: '/contact', label: 'contact' },
  ];
  
  const isActive = (path) => location.pathname === path;
  
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl backdrop-saturate-150 border-b border-black/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-gradient-to-br from-primary to-primary_hover flex items-center justify-center">
              <span className="text-white font-bold text-lg lg:text-xl font-heading">A</span>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg lg:text-xl font-bold font-heading text-text_primary tracking-tight">AfriOne B&D</h1>
              <p className="text-xs text-text_secondary">African Grocery</p>
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label}`}
                className={`relative font-medium text-sm uppercase tracking-widest transition-colors duration-300 ${
                  isActive(link.path) ? 'text-primary' : 'text-text_secondary hover:text-primary'
                }`}
              >
                {t(link.label)}
                {isActive(link.path) && (
                  <motion.div
                    layoutId="nav-underline"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full"
                  />
                )}
              </Link>
            ))}
          </nav>
          
          {/* Right Actions */}
          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-border hover:bg-secondary/50 transition-all duration-300"
              data-testid="language-toggle"
              aria-label="Toggle language"
            >
              <Globe className="w-4 h-4 text-text_secondary" />
              <span className="text-sm font-medium text-text_primary uppercase">{language}</span>
            </button>
            
            {/* Cart Button */}
            <button
              onClick={openCart}
              className="relative p-2 rounded-full hover:bg-secondary/50 transition-all duration-300"
              data-testid="cart-button"
              aria-label="Open cart"
            >
              <ShoppingCart className="w-6 h-6 text-text_primary" />
              {itemCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs font-bold rounded-full flex items-center justify-center"
                >
                  {itemCount}
                </motion.span>
              )}
            </button>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-full hover:bg-secondary/50 transition-all duration-300"
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-border overflow-hidden"
          >
            <nav className="py-4 px-4 space-y-2">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label}`}
                  className={`block px-4 py-3 rounded-xl font-medium transition-colors duration-300 ${
                    isActive(link.path) 
                      ? 'bg-primary/10 text-primary' 
                      : 'text-text_secondary hover:bg-secondary/50'
                  }`}
                >
                  {t(link.label)}
                </Link>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
